import type { Request, ResponseItem, UploadReference } from '@app/src/types/resourceExplorer'
import { distinctBy } from '@app/src/utils/helpersTs'
import { QuestionTypes } from '@app/src/wf-constants'

export type ResponseFile = UploadReference & { requestItemId: number; expiresAt?: string | null }

// Mirrors ALLOWED_FILE_EXTENSIONS in apps/suggested-answers/src/services/upload.service.ts, which rejects
// anything else. File questions accept any type, so images and the like are left out instead of failing.
const REPOSITORY_FILE_EXTENSIONS = new Set([
  'csv',
  'eml',
  'epub',
  'gz',
  'html',
  'json',
  'kml',
  'docx',
  'doc',
  'docm',
  'xlsx',
  'xls',
  'xlsm',
  'pptx',
  'ppt',
  'pptm',
  'msg',
  'xml',
  'odt',
  'ods',
  'odp',
  'pdf',
  'txt',
  'rtf',
  'zip',
])

export const isRepositoryFileType = (fileName: string) => {
  const extensionStart = fileName.lastIndexOf('.')
  if (extensionStart === -1) return false
  return REPOSITORY_FILE_EXTENSIONS.has(fileName.slice(extensionStart + 1).toLowerCase())
}

const isFileQuestion = (questionTypeName?: string) =>
  questionTypeName?.toLowerCase() === QuestionTypes.File.toLowerCase()

export const getResponseFiles = (request?: Request, responseItems?: ResponseItem[]): ResponseFile[] => {
  const fileQuestionIds = new Set(
    request?.sections
      .flatMap(section => section.items)
      .filter(item => isFileQuestion(item.questionType?.name))
      .map(item => item.id),
  )

  const files = (responseItems ?? [])
    .filter(responseItem => fileQuestionIds.has(responseItem.requestItemId))
    .flatMap(responseItem =>
      (responseItem.uploadReferences ?? []).map(file => ({
        ...file,
        requestItemId: responseItem.requestItemId,
        expiresAt: responseItem.expiresAt,
      })),
    )

  return distinctBy(
    files.filter(file => isRepositoryFileType(file.originalFileName)),
    file => file.id,
  )
}

const answerFileKey = (file: ResponseFile) => `${file.requestItemId}:${file.originalFileName}`

export const repositoryCopyKey = (responseItemId: number, fileName: string) => `${responseItemId}:${fileName}`

export const getFilesToOffer = (
  files: ResponseFile[],
  previouslySubmittedFiles: ResponseFile[],
  repositoryCopyKeys: ReadonlySet<string>,
): ResponseFile[] => {
  const previouslySubmittedKeys = new Set(previouslySubmittedFiles.map(answerFileKey))
  return files.filter(
    file =>
      !previouslySubmittedKeys.has(answerFileKey(file)) &&
      !repositoryCopyKeys.has(repositoryCopyKey(file.sourceObjectId, file.originalFileName)),
  )
}
